import { CollapsibleFilters } from '../../../components/common/CollapsibleFilters';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  Sheet,
  Button,
  Input,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  IconButton,
  Select,
  Option,
} from '@mui/joy';
import {
  ArrowsClockwise as Refresh,
  DownloadSimple as Download,
  MagnifyingGlass as Search,
  User as Person,
  CurrencyDollar as AttachMoney,
  TrendUp as TrendingUp,
  CreditCard,
  Envelope as Email,
  Phone,
  CaretLineLeft as FirstPage,
  CaretLineRight as LastPage,
  CaretLeft as NavigateBefore,
  CaretRight as NavigateNext,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { apiAccountingClient } from '../../../lib/apiAccounting';
import type { CustomerBalancesReport } from '../../../types/accounting';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const CustomerBalancesReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CustomerBalancesReport | null>(null);
  const [asOfDate, setAsOfDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'balance' | 'name' | 'lastPayment'>('balance');
  const [sortDesc, setSortDesc] = useState(true);

  // Client-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiAccountingClient.getCustomerBalancesReport(asOfDate);
      setData(response);
    } catch (err) {
      setError('Failed to load customer balances report');
      console.error('Report error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [asOfDate, pageSize]);

  const handleExport = async () => {
    if (!data) return;

    // Create CSV content
    const headers = [
      'Customer Name',
      'Company',
      'Email',
      'Phone',
      'Current Balance',
      'Credit Balance',
      'Total Invoiced',
      'Total Paid',
      'Last Payment Date',
      'Last Payment Amount',
      'Days Since Payment',
      'Invoice Count'
    ];

    const rows = data.customers.map(customer => [
      customer.customer_name,
      customer.company || '',
      customer.email || '',
      customer.phone || '',
      customer.current_balance.toFixed(2),
      customer.credit_balance.toFixed(2),
      customer.total_invoiced.toFixed(2),
      customer.total_paid.toFixed(2),
      customer.last_payment_date || '',
      customer.last_payment_amount?.toFixed(2) || '',
      customer.days_since_payment?.toString() || '',
      customer.invoice_count.toString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-balances-${asOfDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCustomerClick = (customerId: number) => {
    // Navigate to customer detail or open modal
    navigate(`/crm/contacts/${customerId}`);
  };

  // Filter and sort customers
  const filteredCustomers = data?.customers.filter(customer => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      customer.customer_name.toLowerCase().includes(term) ||
      customer.company?.toLowerCase().includes(term) ||
      customer.email?.toLowerCase().includes(term) ||
      customer.phone?.toLowerCase().includes(term)
    );
  }) || [];

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'balance':
        comparison = a.current_balance - b.current_balance;
        break;
      case 'name':
        comparison = a.customer_name.localeCompare(b.customer_name);
        break;
      case 'lastPayment':
        const dateA = a.last_payment_date ? new Date(a.last_payment_date).getTime() : 0;
        const dateB = b.last_payment_date ? new Date(b.last_payment_date).getTime() : 0;
        comparison = dateA - dateB;
        break;
    }

    return sortDesc ? -comparison : comparison;
  });

  // Apply client-side pagination
  const totalCount = sortedCustomers.length;
  const totalPages = Math.ceil(totalCount / (pageSize === -1 ? totalCount : pageSize));
  const paginatedCustomers = pageSize === -1
    ? sortedCustomers
    : sortedCustomers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortDesc, pageSize]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger" variant="soft">
        {error}
      </Alert>
    );
  }

  if (!data) {
    return <Alert color="warning">No data available</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 3 }}>
        <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Customer Balances Report</Typography>
        <Box sx={{ display: 'flex', gap: 1, '& > button': { flex: { xs: 1, sm: '0 0 auto' } } }}>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Download />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Refresh />}
            onClick={fetchReport}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <CollapsibleFilters><Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Input
            type="date"
            placeholder="As of Date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            sx={{ width: 200 }}
          />
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            startDecorator={<Search />}
            sx={{ flex: 1, minWidth: 250 }}
          />
        </Box>
      </Card></CollapsibleFilters>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <AttachMoney color="var(--joy-palette-success-500)" style={{ marginRight: 8 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Receivables
                  </Typography>
                  <Typography level="h3">
                    {formatCurrency(data.summary.total_receivables)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <CreditCard color="var(--joy-palette-primary-500)" style={{ marginRight: 8 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Credits
                  </Typography>
                  <Typography level="h3">
                    {formatCurrency(data.summary.total_credits)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <TrendingUp color="var(--joy-palette-warning-500)" style={{ marginRight: 8 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Net Receivables
                  </Typography>
                  <Typography level="h3">
                    {formatCurrency(data.summary.net_receivables)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Person color="var(--joy-palette-neutral-500)" style={{ marginRight: 8 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Customers
                  </Typography>
                  <Typography level="h3">{data.summary.total_customers}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Person color="var(--joy-palette-success-500)" style={{ marginRight: 8 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Active Customers
                  </Typography>
                  <Typography level="h3">{data.summary.active_customers}</Typography>
                  <Chip size="sm" variant="soft" color="success">
                    With Balance
                  </Chip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <CreditCard color="var(--joy-palette-primary-500)" style={{ marginRight: 8 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Credit Customers
                  </Typography>
                  <Typography level="h3">{data.summary.credit_customers}</Typography>
                  <Chip size="sm" variant="soft" color="primary">
                    Overpaid
                  </Chip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Customer Table */}
      <Card>
        <Typography level="h4" sx={{ mb: 2 }}>Customer Details</Typography>
        <Sheet sx={{ overflow: 'auto' }}>
          <Table
            hoverRow
            stickyHeader
            sx={{
              minWidth: 820,
              '& thead th': { fontWeight: 'bold', backgroundColor: 'background.level1' },
              '& tbody tr:hover': { cursor: 'pointer' },
              '& td': { fontSize: 'sm' }
            }}
          >
            <thead>
              <tr>
                <th onClick={() => { setSortBy('name'); setSortDesc(!sortDesc); }} style={{ cursor: 'pointer' }}>
                  Customer {sortBy === 'name' && (sortDesc ? '↓' : '↑')}
                </th>
                <th>Contact Info</th>
                <th onClick={() => { setSortBy('balance'); setSortDesc(!sortDesc); }} style={{ cursor: 'pointer', textAlign: 'right' }}>
                  Current Balance {sortBy === 'balance' && (sortDesc ? '↓' : '↑')}
                </th>
                <th style={{ textAlign: 'right' }}>Credit Balance</th>
                <th style={{ textAlign: 'right' }}>Total Invoiced</th>
                <th style={{ textAlign: 'right' }}>Total Paid</th>
                <th onClick={() => { setSortBy('lastPayment'); setSortDesc(!sortDesc); }} style={{ cursor: 'pointer', textAlign: 'center' }}>
                  Last Payment {sortBy === 'lastPayment' && (sortDesc ? '↓' : '↑')}
                </th>
                <th style={{ textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCustomers.map((customer) => (
                <tr key={customer.customer_id} onClick={() => handleCustomerClick(customer.customer_id)}>
                  <td>
                    <Box>
                      <Typography level="body-sm" fontWeight="bold">
                        {customer.customer_name}
                      </Typography>
                      {customer.company && (
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                          {customer.company}
                        </Typography>
                      )}
                    </Box>
                  </td>
                  <td>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {customer.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Email size={14} color="var(--joy-palette-text-secondary)" />
                          <Typography level="body-xs">{customer.email}</Typography>
                        </Box>
                      )}
                      {customer.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone size={14} color="var(--joy-palette-text-secondary)" />
                          <Typography level="body-xs">{customer.phone}</Typography>
                        </Box>
                      )}
                    </Box>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Typography
                      level="body-sm"
                      fontWeight="bold"
                      sx={{ color: customer.current_balance > 0 ? 'success.600' : 'text.primary' }}
                    >
                      {formatCurrency(customer.current_balance)}
                    </Typography>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Typography
                      level="body-sm"
                      sx={{ color: customer.credit_balance > 0 ? 'primary.600' : 'text.primary' }}
                    >
                      {formatCurrency(customer.credit_balance)}
                    </Typography>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {formatCurrency(customer.total_invoiced)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {formatCurrency(customer.total_paid)}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {customer.last_payment_date ? (
                      <Box>
                        <Typography level="body-xs">
                          {formatDate(customer.last_payment_date)}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                          {formatCurrency(customer.last_payment_amount || 0)}
                        </Typography>
                        {customer.days_since_payment !== null && (
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            ({customer.days_since_payment} days ago)
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        No payments
                      </Typography>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {customer.current_balance > 0 ? (
                      <Chip
                        size="sm"
                        variant="soft"
                        color={
                          customer.days_since_payment && customer.days_since_payment > 90 ? 'danger' :
                          customer.days_since_payment && customer.days_since_payment > 60 ? 'warning' :
                          'success'
                        }
                      >
                        Active
                      </Chip>
                    ) : customer.credit_balance > 0 ? (
                      <Chip size="sm" variant="soft" color="primary">
                        Credit
                      </Chip>
                    ) : (
                      <Chip size="sm" variant="soft" color="neutral">
                        Paid
                      </Chip>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>

        {sortedCustomers.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography level="body-md" sx={{ color: 'text.secondary' }}>
              No customers found matching your criteria
            </Typography>
          </Box>
        )}

        {/* Pagination Controls */}
        {totalCount > 0 && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}>
            {/* Page Size Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="body-sm">Show:</Typography>
              <Select
                size="sm"
                value={pageSize}
                onChange={(_, value) => {
                  if (value) {
                    setPageSize(value);
                    setCurrentPage(1);
                  }
                }}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={50}>50</Option>
                <Option value={100}>100</Option>
                <Option value={-1}>All</Option>
              </Select>
              <Typography level="body-sm" sx={{ ml: 2 }}>
                {pageSize === -1
                  ? `Showing all ${totalCount} customers`
                  : `Showing ${Math.min((currentPage - 1) * pageSize + 1, totalCount)}-${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} customers`
                }
              </Typography>
            </Box>

            {/* Navigation Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                <FirstPage />
              </IconButton>
              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                <NavigateBefore />
              </IconButton>

              {/* Page Numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }

                if (page < 1 || page > totalPages) return null;

                return (
                  <Button
                    key={page}
                    size="sm"
                    variant={page === currentPage ? 'solid' : 'outlined'}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                );
              })}

              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === totalPages || pageSize === -1}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                <NavigateNext />
              </IconButton>
              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === totalPages || pageSize === -1}
                onClick={() => setCurrentPage(totalPages)}
              >
                <LastPage />
              </IconButton>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
};
